import React, { useEffect, useState, useMemo } from 'react';
import { fetchAllUsers } from '../services/userService';
import { fetchContacts } from '../services/contactsService';
import type { AccountUser, AccessPolicy, Contact } from '../types';
import { GRADE_LABELS, ACCESS_POLICY_LABELS, isCompanyDomain } from '../types';
import UserPanel from '../components/UserPanel';
import ContactSlideOver from '../components/ContactSlideOver';
import InviteUserModal from '../components/InviteUserModal';

// Relative-time helper. Tiny inline impl — date-fns isn't a dep here.
function timeAgo(ts: any): string {
  if (!ts) return '—';
  const ms = typeof ts === 'object' && ts.toMillis ? ts.toMillis() : typeof ts === 'number' ? ts : new Date(ts).getTime();
  if (!Number.isFinite(ms)) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

const UsersPage: React.FC = () => {
  const [users, setUsers]           = useState<AccountUser[]>([]);
  const [contacts, setContacts]     = useState<Contact[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPolicy, setFilterPolicy] = useState<AccessPolicy | ''>('');
  const [selected, setSelected]     = useState<AccountUser | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    // Load users + contacts in parallel. Contacts enrich users with HRIS data
    // (jobTitle, department) and surface usedBy.{okr|epr|portal} badges.
    // When contacts/ is empty (pre-migration) the page degrades gracefully —
    // existing user-only fields still render.
    Promise.all([
      fetchAllUsers(),
      fetchContacts({ status: 'active' }).catch(() => [] as Contact[]),
    ])
      .then(([u, c]) => { setUsers(u); setContacts(c); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  /** email → contact lookup for fast row enrichment. */
  const contactByEmail = useMemo(() => {
    const map = new Map<string, Contact>();
    for (const c of contacts) {
      const email = c.email?.toLowerCase()?.trim();
      if (email) map.set(email, c);
    }
    return map;
  }, [contacts]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(u => {
      const matchSearch  = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || (u.jobTitle ?? '').toLowerCase().includes(q);
      const matchGrade   = !filterGrade  || u.grade === filterGrade;
      const matchStatus  = !filterStatus || (filterStatus === 'active' ? u.isActive !== false : u.isActive === false);
      const matchPolicy  = !filterPolicy || (u.access_policy || 'full_access') === filterPolicy;
      return matchSearch && matchGrade && matchStatus && matchPolicy;
    });
  }, [users, search, filterGrade, filterStatus, filterPolicy]);

  /** Contacts that are NOT linked to any user — typically M365-synced people
   *  who don't have a Firebase Auth account yet. Shown as additional rows so
   *  admin can edit them in-place. After "Grant Login" from ContactSlideOver,
   *  they become a regular user row. */
  const contactOnlyFiltered = useMemo(() => {
    const userEmails = new Set(users.map((u) => u.email?.toLowerCase()?.trim()).filter(Boolean));
    const q = search.toLowerCase();
    return contacts.filter((c) => {
      const email = c.email?.toLowerCase()?.trim();
      if (!email || userEmails.has(email)) return false;
      const matchSearch  = !q
        || c.displayName?.toLowerCase().includes(q)
        || c.email?.toLowerCase().includes(q)
        || c.jobTitle?.toLowerCase().includes(q);
      const matchStatus  = !filterStatus || (filterStatus === 'active' ? c.status === 'active' : c.status === 'inactive');
      return matchSearch && matchStatus;
    });
  }, [users, contacts, search, filterStatus]);

  const handleSaved = (updated: AccountUser) => {
    setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
    setSelected(updated);
  };

  // Shared classes for filter inputs — calm stone, no brand navy.
  const filterCls = 'border border-stone-200 rounded-md px-3 py-2 text-sm bg-white text-stone-700 hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition';

  return (
    <div className="max-w-6xl mx-auto px-8 py-10">

      {/* Header */}
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">People</h1>
          <p className="text-sm text-stone-500 mt-1 tabular-nums">
            {users.length + contactOnlyFiltered.length} people
            {contactOnlyFiltered.length > 0 && (
              <span className="text-stone-400"> · {users.length} with login · {contactOnlyFiltered.length} contact-only</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 rounded-md transition shrink-0"
        >
          <span className="material-symbols-rounded" style={{ fontSize: 16 }}>add</span>
          Invite person
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <div className="relative flex-1 min-w-56">
          <span className="material-symbols-rounded absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" style={{ fontSize: 16 }}>search</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, email, title…"
            className="w-full pl-9 pr-3 py-2 border border-stone-200 rounded-md text-sm bg-white text-stone-800 placeholder:text-stone-400 hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition"
          />
        </div>

        <select value={filterGrade} onChange={e => setFilterGrade(e.target.value)} className={filterCls}>
          <option value="">All grades</option>
          {Object.entries(GRADE_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={filterCls}>
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="inactive">Suspended</option>
        </select>

        <select value={filterPolicy} onChange={e => setFilterPolicy(e.target.value as AccessPolicy | '')} className={filterCls}>
          <option value="">All access</option>
          {Object.entries(ACCESS_POLICY_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>

      {/* Table — borderless, hairline-divided, Linear/Notion feel */}
      <div className="bg-white">
        {loading ? (
          <div className="py-10 space-y-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-stone-100 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-stone-100 rounded w-44" />
                  <div className="h-2.5 bg-stone-50 rounded w-60" />
                </div>
                <div className="h-3 w-20 bg-stone-50 rounded" />
                <div className="h-3 w-16 bg-stone-50 rounded" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 flex flex-col items-center gap-2 text-center">
            <span className="material-symbols-rounded text-stone-300" style={{ fontSize: 28 }}>search_off</span>
            <p className="text-sm font-medium text-stone-700">No people match your filters</p>
            <p className="text-xs text-stone-500">Try clearing the search or status filter.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100">
                <th className="text-left px-3 py-2.5 text-[11px] font-medium text-stone-400 uppercase tracking-wider">Person</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-medium text-stone-400 uppercase tracking-wider hidden md:table-cell">Role</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-medium text-stone-400 uppercase tracking-wider hidden lg:table-cell">Team</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-medium text-stone-400 uppercase tracking-wider">Status</th>
                <th className="text-left px-3 py-2.5 text-[11px] font-medium text-stone-400 uppercase tracking-wider hidden md:table-cell">Last active</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(user => {
                const isActive   = user.isActive !== false;
                const isPending  = user.status === 'invited';
                const lastActive = (user as any).lastSignInAt;
                const company    = isCompanyDomain(user.email);
                const contact    = contactByEmail.get(user.email?.toLowerCase()?.trim() ?? '');

                return (
                  <tr
                    key={user.id}
                    onClick={() => setSelected(user)}
                    className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
                  >
                    {/* Person */}
                    <td className="px-3 py-4">
                      <div className="flex items-center gap-3">
                        {user.photoURL || user.avatar
                          ? <img src={user.photoURL || user.avatar} className="w-10 h-10 rounded-full flex-shrink-0 ring-1 ring-stone-100" alt="" />
                          : <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 font-medium text-sm flex-shrink-0">
                              {user.name?.[0]?.toUpperCase() ?? '?'}
                            </div>
                        }
                        <div className="min-w-0">
                          <p className="font-medium text-stone-900 text-sm truncate">{user.name}</p>
                          <p className="text-xs text-stone-500 truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Role — enriched from contact if user.jobTitle empty */}
                    <td className="px-3 py-4 hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-stone-700">
                          {user.jobTitle
                            ?? contact?.jobTitle
                            ?? <span className="text-stone-400">—</span>}
                        </span>
                        {/* usedBy badges from contact */}
                        {contact?.usedBy && (
                          <div className="flex items-center gap-1">
                            {contact.usedBy.okr    && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" title="OKR Tracker user">OKR</span>}
                            {contact.usedBy.epr    && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700" title="EPR user">EPR</span>}
                            {contact.usedBy.portal && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-stone-100 text-stone-700" title="Account Portal user">PORTAL</span>}
                            {contact.usedBy.kpi    && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" title="KPI user">KPI</span>}
                          </div>
                        )}
                        {contact?.source === 'm365-sync' && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700" title="Synced from Microsoft 365">M365</span>
                        )}
                      </div>
                    </td>

                    {/* Team — enriched from contact */}
                    <td className="px-3 py-4 hidden lg:table-cell">
                      <span className="text-sm text-stone-700">
                        {user.department
                          ?? contact?.department
                          ?? <span className="text-stone-400">—</span>}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-3 py-4">
                      <div className="inline-flex items-center gap-2 text-sm">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                          !isActive   ? 'bg-red-500'
                        : isPending   ? 'bg-amber-500'
                        : 'bg-emerald-500'
                        }`} />
                        <span className="text-stone-700">
                          {!isActive ? 'Suspended' : isPending ? 'Pending' : 'Active'}
                        </span>
                        {!company && <span className="text-[11px] text-stone-400">· external</span>}
                      </div>
                    </td>

                    {/* Last active */}
                    <td className="px-3 py-4 hidden md:table-cell">
                      <span className="text-sm text-stone-500 tabular-nums">{timeAgo(lastActive)}</span>
                    </td>
                  </tr>
                );
              })}

              {/* Contact-only rows — M365 synced people without app login */}
              {contactOnlyFiltered.map((c) => (
                <tr
                  key={`contact-${c.id}`}
                  onClick={() => setSelectedContact(c)}
                  className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-4">
                    <div className="flex items-center gap-3">
                      {c.photoURL
                        ? <img src={c.photoURL} className="w-10 h-10 rounded-full flex-shrink-0 ring-1 ring-stone-100" alt="" />
                        : <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 font-medium text-sm flex-shrink-0">
                            {c.displayName?.[0]?.toUpperCase() ?? '?'}
                          </div>
                      }
                      <div className="min-w-0">
                        <p className="font-medium text-stone-900 text-sm truncate">{c.displayName}</p>
                        <p className="text-xs text-stone-500 truncate">{c.email}</p>
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-4 hidden md:table-cell">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-stone-700">
                        {c.jobTitle ?? <span className="text-stone-400">—</span>}
                      </span>
                      {c.usedBy?.okr    && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">OKR</span>}
                      {c.usedBy?.epr    && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">EPR</span>}
                      {c.usedBy?.kpi    && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">KPI</span>}
                      {c.source === 'm365-sync' && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700" title="Synced from Microsoft 365">M365</span>
                      )}
                      {c.needsReview && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" title="Needs HR review">REVIEW</span>
                      )}
                    </div>
                  </td>

                  <td className="px-3 py-4 hidden lg:table-cell">
                    <span className="text-sm text-stone-700">
                      {c.department ?? <span className="text-stone-400">—</span>}
                    </span>
                  </td>

                  <td className="px-3 py-4">
                    <div className="inline-flex items-center gap-2 text-sm">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                        c.status === 'inactive' ? 'bg-stone-300' : 'bg-stone-400'
                      }`} />
                      <span className="text-stone-600">
                        {c.status === 'inactive' ? 'Inactive' : 'Contact only'}
                      </span>
                      {c.isExternal && <span className="text-[11px] text-stone-400">· external</span>}
                    </div>
                  </td>

                  <td className="px-3 py-4 hidden md:table-cell">
                    <span className="text-sm text-stone-400">—</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Slide-over for users with login */}
      <UserPanel
        user={selected}
        onClose={() => setSelected(null)}
        onSaved={handleSaved}
      />

      {/* Slide-over for contact-only (M365-synced people without login) */}
      <ContactSlideOver
        contact={selectedContact}
        onClose={() => setSelectedContact(null)}
        onChanged={() => { reload(); }}
        onMergeRequested={() => { /* no-op on People page */ }}
      />

      {/* Invite User Modal */}
      <InviteUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => { reload(); }}
      />
    </div>
  );
};

export default UsersPage;
